// Engine-chrome navigation (v5 routing ruling, 2026-08-16; the flip, 2026-09-20).
//
// Every engine surface has two spellings: the ORIGINAL path a member already
// knows (/home, /community, /books/…) and the -next alias it lived at while
// the legacy page still owned the original. Which one the app navigates to is
// decided in exactly one place — here — by ENGINE_CUTOVER:
//
//   flag off  the legacy page owns /home; the engine answers at /home-next
//   flag on   the engine owns /home; /home-next still answers, as a redirect
//
// Eddie, on the first flipped build: "this is showing home-next instead of
// /home when it cutover … i want it to retain the original domains." So at
// the flip the original paths are canonical, and nothing in the app should
// spell a -next path by hand.
import { ENGINE_CUTOVER } from '@/config/engineFlags';

/** [alias, canonical]. Prefix pairs cover their whole subtree. */
export const ENGINE_ALIASES = [
  ['/home-next', '/home'],
  ['/community-next', '/community'],
  ['/library-next', '/library'],
  ['/missions-next', '/missions'],
  ['/more-next', '/more'],
  ['/profile-next', '/profile/edit'],
  ['/payment-manage-next', '/payment-manage'],
  ['/arc-next', '/arc'],
  ['/review-next', '/review'],
  ['/challenges-next', '/challenges'],
  ['/archive-next', '/books'],
  ['/admin-next', '/admin'],
];

// a path, a query string or a subpath — never a longer word that merely
// starts the same way (/home-next must not match /home-nextdoor)
function swap(p, from, to) {
  const s = String(p || '');
  if (s === from || s.startsWith(`${from}/`) || s.startsWith(`${from}?`) || s.startsWith(`${from}#`)) return to + s.slice(from.length);
  return null;
}

/** The original spelling of an engine path, whichever spelling you hold. */
export function toCanonical(p) {
  for (const [alias, canon] of ENGINE_ALIASES) { const r = swap(p, alias, canon); if (r !== null) return r; }
  return String(p || '');
}

/** The -next spelling of an engine path, whichever spelling you hold. */
export function toAlias(p) {
  for (const [alias, canon] of ENGINE_ALIASES) { const r = swap(p, canon, alias); if (r !== null) return r; }
  return String(p || '');
}

/** The path the app should navigate to for an engine surface right now. */
export function enginePath(p) {
  return ENGINE_CUTOVER ? toCanonical(p) : toAlias(p);
}

/** Where a signed-in member lands (landing page, login, signup): the 챌린지
 *  tab since 2026-10. Before the flip there is no 챌린지 tab at that path, so
 *  the flag-off build keeps the legacy home. */
export const MEMBER_LANDING = ENGINE_CUTOVER ? '/challenges' : '/home';

/** What the home is called. 챌린지 took the first tab (2026-10) and the engine
 *  home became 데일리 루틴: the label only, the page and its address are the
 *  same. The legacy home (flag off) is still 홈. */
export const HOME_NAME = ENGINE_CUTOVER ? '데일리 루틴' : '홈';

// The six pairs the legacy pages have always been twinned on. chromePath keeps
// to these so the flag-off build behaves exactly as it did (engine.flagoff.spec
// pins it); the wider list above is for the engine's own links and the router.
const CHROME_TWINS = ['/home', '/community', '/library', '/missions', '/challenges', '/books'];

/** A shared page rendered INSIDE the engine chrome (route name Engine*) keeps
 *  the member in that chrome when it navigates onward. Reached at its legacy
 *  path it keeps legacy targets. At the flip this collapses to identity. */
export function chromePath(route, path) {
  if (ENGINE_CUTOVER) return path;
  if (!route || !String(route.name || '').startsWith('Engine')) return path;
  for (const canon of CHROME_TWINS) { const r = swap(path, canon, toAlias(canon)); if (r !== null) return r; }
  return path;
}
